import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { ReviewCard } from './ReviewCard'

const VERDICT: Record<string, { labelFa: string; badge: string }> = {
  awarded: { labelFa: 'اخذ شده', badge: 'badge--good' },
  conditional: { labelFa: 'مشروط', badge: 'badge--warn' },
  not_yet: { labelFa: 'هنوز نه', badge: '' },
}

export function CertificatePage() {
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['certificate'], queryFn: api.certificate })

  const answer = useMutation({
    mutationFn: (input: { itemNo: number; answer: 'yes' | 'no' | 'partial' }) =>
      api.answerReadiness(input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['certificate'] }),
  })

  if (isLoading || !data) {
    return (
      <div className="container center">
        <p className="muted">در حال بارگذاری…</p>
      </div>
    )
  }

  return (
    <div className="container stack">
      <div className="card">
        <h1>مسیر نشان افرا</h1>
        <p className="meta">
          چهار نشان، برای چهار سطح توسعه. قاعده هر چهار سطح یکی است — ۷۰٪ امتیاز آن سطح، امتیاز ۲ یا
          ۳ در همه گزاره‌های کلیدی آن سطح، و هیچ بُعدی زیر ۵۰٪ — اما هر بار روی مجموعه دشوارتری از
          گزاره‌ها اعمال می‌شود.
        </p>

        <div className="row" style={{ marginBottom: 'var(--space-4)' }}>
          {data.awardedTier ? (
            <span className="badge badge--good">نشان فعلی: {data.awardedTier}</span>
          ) : (
            <span className="badge">هنوز نشانی اخذ نشده</span>
          )}
          {data.conditionalTier && (
            <span className="badge badge--warn">قابل اخذ به صورت مشروط: {data.conditionalTier}</span>
          )}
        </div>

        {data.nextStepFa && (
          <div className="notice notice--teach">
            <strong>گام بعدی:</strong> {data.nextStepFa}
          </div>
        )}

        {/* Not a footnote. The key-statement list is a lower bound and the
            badge↔level mapping was read off §7's sub-section titles, so
            presenting this as an official verdict would be the same sin the
            standard itself begins by refusing. */}
        {!data.authoritative && (
          <div className="notice notice--info" style={{ marginTop: 'var(--space-4)' }}>
            <strong>این محاسبه هنوز رسمی نیست.</strong>
            <ul style={{ margin: 'var(--space-2) 0 0', paddingInlineStart: 'var(--space-5)' }}>
              {data.caveatsFa.map((caveat) => (
                <li key={caveat} style={{ marginBottom: 'var(--space-2)' }}>
                  {caveat}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {data.levels.map((level) => {
        const verdict = VERDICT[level.verdict] ?? VERDICT.not_yet
        const keyMet = level.keyStatements.filter((k) => k.meets).length
        return (
          <div key={level.levelNo} className="card">
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <h2 style={{ margin: 0 }}>
                نشان {level.tier}
                <span className="meta" style={{ fontWeight: 400 }}>
                  {' '}
                  · {level.levelTitleFa}
                </span>
              </h2>
              <span className={`badge ${verdict?.badge ?? ''}`}>{verdict?.labelFa}</span>
            </div>

            <div style={{ margin: 'var(--space-4) 0' }}>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="meta">
                  امتیاز این سطح — <span className="num">{level.known}</span> از{' '}
                  <span className="num">{level.total}</span> گزاره روشن شده
                </span>
                <span className="dimension__score">
                  <span className="num">{level.percent}</span>٪
                </span>
              </div>
              <div className="rail-bar">
                <div className="rail__fill" style={{ width: `${Math.min(100, level.percent)}%` }} />
              </div>
              <p className="meta" style={{ marginTop: 'var(--space-1)' }}>
                گزاره‌ای که هنوز درباره‌اش نپرسیده‌ایم صفر حساب می‌شود — نشان ادعایی است درباره آنچه
                نشان داده‌اید، نه آنچه هنوز پرسیده نشده.
              </p>
            </div>

            {level.conditionalReasonFa && (
              <div className="notice notice--info">{level.conditionalReasonFa}</div>
            )}

            <div className="row" style={{ marginTop: 'var(--space-3)' }}>
              <span className={`badge ${keyMet === level.keyStatements.length ? 'badge--good' : 'badge--warn'}`}>
                گزاره‌های کلیدی: <span className="num">{keyMet}</span> از{' '}
                <span className="num">{level.keyStatements.length}</span>
              </span>
              {level.dimensions
                .filter((d) => !d.meets)
                .map((d) => (
                  <span key={d.code} className="badge badge--bad">
                    {d.titleFa} <span className="num">{d.percent}</span>٪
                  </span>
                ))}
              {level.validityMonths && (
                <span className="badge">
                  اعتبار <span className="num">{level.validityMonths}</span> ماه
                </span>
              )}
            </div>

            {level.blockingFa.length > 0 && (
              <ul className="meta" style={{ marginTop: 'var(--space-3)', paddingInlineStart: 'var(--space-5)' }}>
                {level.blockingFa.slice(0, 6).map((blocker) => (
                  <li key={blocker} style={{ marginBottom: 'var(--space-1)' }}>
                    {blocker}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )
      })}

      <ReviewCard />

      <div className="card">
        <h2>دروازه آمادگی</h2>
        <p className="meta">
          پیش‌شرط هر نشانی. <span className="num">{data.readiness.requiredMet}</span> از{' '}
          <span className="num">{data.readiness.requiredTotal}</span> مورد الزامی انجام شده است.
        </p>
        {data.readiness.items.map((item) => (
          <div key={item.no} style={{ padding: 'var(--space-3) 0', borderTop: '1px solid var(--line)' }}>
            <div style={{ lineHeight: 1.9 }}>
              <span className="meta">
                <span className="num">{item.no}</span>.{' '}
              </span>
              {item.textFa}
              {item.kind === 'document' && (
                <span className="badge badge--warn" style={{ marginInlineStart: 8 }}>
                  نیازمند سند
                </span>
              )}
            </div>
            {item.kind === 'document' ? (
              <p className="meta">
                {item.documentId
                  ? 'سند بارگذاری شده است.'
                  : 'برای این مورد باید فایلش را در بخش «اسناد» بارگذاری کنید؛ پاسخ بله کافی نیست.'}
              </p>
            ) : (
              <div className="row" style={{ marginTop: 'var(--space-2)' }}>
                {(['yes', 'partial', 'no'] as const).map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={`btn btn--sm ${item.answer === value ? '' : 'btn--quiet'}`}
                    disabled={answer.isPending}
                    onClick={() => answer.mutate({ itemNo: item.no, answer: value })}
                  >
                    {value === 'yes' ? 'بله' : value === 'partial' ? 'تا حدی' : 'خیر'}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
